import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDashboardControls } from 'teacher-ui-client/components/reporting/dashboard-controls'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import DataTableOperations from '@blakeelearning/data-tables/components/data-table-operations'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Tooltip from 'teacher-ui-client/components/tooltip'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import RouteTemplate from 'ember-route-template'
import { t } from 'ember-intl'
import type { ReDashboardTableRoute } from 'teacher-ui-client/reporting/ui-scope/re/dashboard/table/route'
import { array, fn } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { on } from '@ember/modifier'
import { eq } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type RouterService from '@ember/routing/router-service'
import type SessionService from 'teacher-ui-client/services/session'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { tracked } from '@glimmer/tracking'
import { isPresent } from '@ember/utils'
import { statefulSort, type TableAction } from 'teacher-ui-client/utils/giraffe/stateful-sort'
import type { PhosphorIconName } from 'phosphor-icons'

interface Signature {
  Args: {
    model: ModelFor<ReDashboardTableRoute>
  }
}
export class ReportingReDashboardTablePage extends Component<Signature> {
  @service router!: RouterService
  @service session!: SessionService

  readonly routePrefix = 'reporting.ui-scope.re.dashboard'

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Reading)
  }

  get studentColspan() {
    return this.isSubco ? 5 : 3
  }

  // These are set by statefulSort action
  @tracked currentSortIcon: PhosphorIconName = 'phosphor-caret-up-down-fill'
  @tracked currentSortColumn?: string

  get isSummaryDataPresent() {
    return isPresent(this.args.model?.summary)
  }

  get summaryRow() {
    const totals = this.args.model?.summary?.school
    return {
      currentLessonLexile: totals?.average_current_lesson_lexile,
      initialLessonLexile: totals?.average_initial_lesson_lexile,
      lexileGrowth: totals?.average_lexile_growth,
      usageScore: totals?.average_usage_score,
      awardsEarnedBronze: totals?.awards_earned_bronze,
      awardsEarnedGold: totals?.awards_earned_gold,
      awardsEarnedSilver: totals?.awards_earned_silver,
      booksRead: totals?.books_read,
      drivingTestsAttempted: totals?.driving_tests_attempted,
      drivingTestsCompleted: totals?.driving_tests_completed,
      lessonsCompleted: totals?.lessons_completed,
      skillsMasteredTotal: totals?.skills_mastered_total,
      mapQuizzesAttempted: totals?.map_quizzes_attempted,
      mapQuizzesAverageScorePercent: totals?.map_quizzes_average_score_percent,
      spellingCompleted: totals?.spelling_completed,
      storiesWritten: totals?.stories_written,
      studentCount: totals?.student_count,
      totalTime: totals?.total_time,
    }
  }

  get rows() {
    const statsData = this.args.model.summary?.students

    return this.args.model.scopedStudents.map((student) => {
      const foundStatsDataForStudent = statsData?.find((item) => String(item?.student_id) === student.id) ?? {}
      return {
        studentId: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        studentExternalId: student.externalId,
        gradePosition: student.gradePosition,
        ...foundStatsDataForStudent,
        teacherName: student
          .teachersFor(SubscriptionType.Reading)
          .map((teacher) => teacher.fullName)
          .join(', '),
        schoolClasses: student
          .schoolClassesFor(SubscriptionType.Reading)
          .map((schoolClass) => schoolClass.name)
          .join(', '),
      }
    })
  }

  statefulSort = (tableAction: TableAction, tableActionOperation: string, tableActionKey: string) => {
    return statefulSort(this, tableAction, tableActionOperation, tableActionKey)
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.re.student-summary.table', studentId)
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.dashboardTitles.re"}} />
    <ReportingDashboardControls
      @students={{@model.scopedStudents}}
      @routePrefix={{this.routePrefix}}
      @exportOptions={{array ExportType.CSV}}
      @isDataPresent={{this.isSummaryDataPresent}}
      @studentRouteName="reporting.ui-scope.re.student-summary.table"
    />
    {{#if this.isSummaryDataPresent}}
      <ContentTile>
        <div class="max-h-192 overflow-auto print:max-h-[unset] print:overflow-visible">
          <DataTableOperations @data={{this.rows}} as |transformedData tableAction|>
            <table class="table-print-zoom-1/2 print:divide-y print:divide-black" data-test-dashboard-table exportable>
              <thead class="sticky top-0 font-semibold print:divide-y print:divide-black">
                {{! Using box shadow, we can get a border between the header rows, even with the sticky header }}
                <tr
                  class="bg-oceany-blue-150 divide-x divide-white text-center text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
                >
                  <th class="p-3" colspan={{this.studentColspan}}>
                    {{t "reporting.dashboardTables.students"}}
                  </th>
                  {{! for studentExternalId column }}
                  <th class="hidden" />
                  <th class="p-3" colspan="3">
                    {{t "reporting.dashboardTables.lessonsCompleted.re"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.skillsMastered"}}
                  </th>
                  <th class="p-3" colspan="2">
                    {{t "reporting.dashboardTables.quizzesCompleted.re"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.initialLexile"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.highestLexile"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.lexileGrowth"}}
                  </th>
                  <th class="p-3" colspan="2">
                    {{t "reporting.dashboardTables.drivingTests"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.booksRead"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.storiesWritten"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.spellingLessonsCompleted" htmlSafe=true}}
                  </th>
                  <th class="p-3" colspan="3">
                    {{t "reporting.dashboardTables.certificates"}}
                  </th>
                  <th class="p-3 whitespace-nowrap">
                    {{t "reporting.dashboardTables.usage"}}
                    <span class="print:hidden">
                      <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 cursor-pointer" />
                      <Tooltip @text={{t "reporting.dashboardTables.usageTooltip.re"}} role="presentation" />
                    </span>
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.timeInProgram"}}
                  </th>
                </tr>
                <tr
                  class="bg-oceany-blue-100 divide-x divide-white text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
                >
                  <th
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "firstName")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "firstName")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "lastName")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lastName")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th class="hidden">
                    {{t "reporting.dashboardTables.studentExternalId"}}
                  </th>
                  <th
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "schoolClasses")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.classes"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "schoolClasses")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  {{#if this.isSubco}}
                    <th
                      class="min-w-40 p-3 select-none"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "teacherName")}}
                      role="button"
                    >
                      <div class="flex items-center justify-between gap-1">
                        {{t "reporting.dashboardTables.teacher"}}
                        <PhosphorIcon
                          @icon={{if
                            (eq this.currentSortColumn "teacherName")
                            this.currentSortIcon
                            "phosphor-caret-up-down-fill"
                          }}
                          class="shrink-0 print:hidden"
                          role="presentation"
                        />
                      </div>
                    </th>
                    <th
                      class="p-3 select-none"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "gradePosition")}}
                      role="button"
                    >
                      <div class="flex items-center justify-between gap-1">
                        {{t "grade.label"}}
                        <PhosphorIcon
                          @icon={{if
                            (eq this.currentSortColumn "gradePosition")
                            this.currentSortIcon
                            "phosphor-caret-up-down-fill"
                          }}
                          class="shrink-0 print:hidden"
                          role="presentation"
                        />
                      </div>
                    </th>
                  {{/if}}
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "initial_lesson_number")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.initialLesson.re" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "initial_lesson_number")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "current_lesson_number")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.highestLessonCompleted.re" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "current_lesson_number")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="relative p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lessons_completed")}}
                    role="button"
                  >
                    <div class="flex items-center gap-1">
                      {{t "reporting.dashboardTables.totalCompleted"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lessons_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="ml-auto shrink-0 print:hidden"
                        role="presentation"
                      />
                      {{#if this.isSubco}}
                        <span class="print:hidden">
                          <PhosphorIcon
                            @icon="phosphor-info-fill"
                            class="text-dusty-black-500 cursor-pointer"
                            role="presentation"
                          />
                          <Tooltip @text={{t "reporting.dashboardToolTipTotals"}} role="presentation" />
                        </span>
                      {{/if}}
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "skills_mastered")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "skills_mastered")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "map_quizzes_attempted")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.numberOfAttempts" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "map_quizzes_attempted")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "map_quizzes_average_score_percent")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.averageLessonQuizScore.re" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "map_quizzes_average_score_percent")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "initial_lesson_lexile")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "initial_lesson_lexile")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "current_lesson_lexile")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "current_lesson_lexile")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lexile_growth")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lexile_growth")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "driving_tests_completed")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.testsMastered" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "driving_tests_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "driving_tests_attempted")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.totalAttempts" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "driving_tests_attempted")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "books_read")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "books_read")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "stories_written")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "stories_written")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "spelling_completed")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "spelling_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_gold")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.gold"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "awards_earned_gold")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_silver")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.silver"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "awards_earned_silver")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_bronze")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.bronze"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "awards_earned_bronze")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "usage_score")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "usage_score")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "total_time")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.totalHours"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "total_time")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                </tr>
                <tr class="bg-oceany-blue-100 text-black shadow-[inset_0_-1px_0] shadow-white" data-test-summary-row>
                  <th class="p-3" colspan={{this.studentColspan}}>
                    {{t "reporting.dashboardTables.totals"}}
                  </th>
                  {{! for studentExternalId column }}
                  <th class="hidden" />
                  <th class="p-3">
                    -
                  </th>
                  <th class="p-3">
                    -
                  </th>
                  <th data-test-school-lessons-completed class="p-3">
                    {{formatValue this.summaryRow.lessonsCompleted}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.skillsMasteredTotal}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.mapQuizzesAttempted}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.mapQuizzesAverageScorePercent append="%"}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.initialLessonLexile}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.currentLessonLexile}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.lexileGrowth}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.drivingTestsCompleted}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.drivingTestsAttempted}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.booksRead}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.storiesWritten}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.spellingCompleted}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.awardsEarnedGold}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.awardsEarnedSilver}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.awardsEarnedBronze}}
                  </th>
                  <th class="p-3">
                    {{formatValue this.summaryRow.usageScore}}
                  </th>
                  <th class="p-3">
                    {{formatDuration this.summaryRow.totalTime "-"}}
                  </th>
                </tr>
              </thead>
              <tbody class="print:divide-dusty-black-100 print:divide-y">
                {{#each transformedData as |row index|}}
                  {{! template-lint-disable no-invalid-interactive }}
                  <tr
                    data-test-table-row={{index}}
                    class="even:bg-oceany-blue-50 hover:bg-oceany-blue-100 cursor-pointer bg-white"
                    {{on "click" (fn this.transitionToStudentRoute row.studentId)}}
                  >
                    <td class="text-oceany-blue-300 p-3 font-bold wrap-anywhere">
                      {{row.firstName}}
                    </td>
                    <td class="text-oceany-blue-300 p-3 font-bold wrap-anywhere">
                      {{row.lastName}}
                    </td>
                    <td class="hidden">
                      {{row.studentExternalId}}
                    </td>
                    <td class="p-3">
                      {{row.schoolClasses}}
                    </td>
                    {{#if this.isSubco}}
                      <td class="p-3">
                        {{row.teacherName}}
                      </td>
                      <td class="p-3">
                        {{gradeName row.gradePosition}}
                      </td>
                    {{/if}}
                    <td class="p-3">
                      {{formatValue row.initial_lesson_number}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.current_lesson_number}}
                    </td>
                    <td data-test-student-lessons-completed class="p-3">
                      {{formatValue row.lessons_completed}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.skills_mastered}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.map_quizzes_attempted}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.map_quizzes_average_score_percent append="%"}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.initial_lesson_lexile}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.current_lesson_lexile}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.lexile_growth}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.driving_tests_completed}}
                    </td>
                    <td>
                      {{formatValue row.driving_tests_attempted}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.books_read}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.stories_written}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.spelling_completed}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.awards_earned_gold}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.awards_earned_silver}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.awards_earned_bronze}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.usage_score}}
                    </td>
                    <td class="p-3">
                      {{formatDuration row.total_time "-"}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </DataTableOperations>
        </div>
      </ContentTile>
    {{else}}
      <NoDataMessage />
    {{/if}}
  </template>
}

export default RouteTemplate(ReportingReDashboardTablePage)
